import React, { ReactElement, useCallback, useState, useEffect, useRef, useMemo, CSSProperties } from "react";
import { DecisionToggleProps, DecisionToggleState } from "./DecisionToggle.types";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { useWindow } from "@fluentui/react";
import classNames from "classnames";

const defaultRailWidthPx = 126;
const handleWidthPx = 42;

type SlideStartEvent
    = React.MouseEvent<HTMLDivElement>
    | React.TouchEvent<HTMLDivElement>;

type SlideEvent
    = MouseEvent
    | TouchEvent;

function getPositionFromValue(
    value: boolean | undefined,
    railWidthPx: number = defaultRailWidthPx,
): { handleLeft: number, handleRight: number } {
    if (value === undefined) {
        const midDistance = (railWidthPx - handleWidthPx) / 2;

        return {
            handleLeft: midDistance,
            handleRight: midDistance,
        };
    }

    const farDistance = (railWidthPx - handleWidthPx);

    return value
        ? { handleLeft: farDistance, handleRight: 0 }
        : { handleLeft: 0, handleRight: farDistance };
}

/**
 * A decision toggle component that allows users to switch between two states
 * with the option of a neutral initial state. The value can be set or toggled 
 * either by clicking the different options or by dragging the handle - either
 * by touch or by mouse.
 *
 * @param {DecisionToggleProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function DecisionToggle({
    clickable = false,
    disabled = false,
    fullWidth = false,
    onChange,
    value,
}: DecisionToggleProps): ReactElement | null {
    const wnd = useWindow();
    const rootEl = useRef<HTMLDivElement>(null);
    const [railWidthPx, setRailWidthPx] = useState<number>(defaultRailWidthPx);
    const [state, setState] = useState<DecisionToggleState>({
        ...getPositionFromValue(value, railWidthPx),
        isDragging: false,
    });

    const {
        handleLeft,
        handleRight,
        isDragging,
        dragStartX,
    } = state;

    const onDragStart = useCallback((event: SlideStartEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) {
            return;
        }

        const dragStartX = "touches" in event
            ? event.touches[0].clientX
            : event.clientX;

        setState((prevState) => ({
            ...prevState,
            isDragging: true,
            dragStartX,
        }));
    }, [disabled, setState]);

    const onDragMove = useCallback((event: SlideEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (!isDragging || dragStartX === undefined) {
            return;
        }

        const clientX = "touches" in event
            ? event.touches[0].clientX
            : event.clientX;

        const deltaX = clientX - dragStartX;
        const newLeft = Math.max(0, Math.min(railWidthPx - handleWidthPx, handleLeft + deltaX));
        const newRight = railWidthPx - handleWidthPx - newLeft;

        setState((prevState) => ({
            ...prevState,
            handleLeft: newLeft,
            handleRight: newRight,
            dragStartX: clientX, // Update drag start position for next move
        }));
    }, [isDragging, dragStartX, handleLeft, railWidthPx]);

    const onDragEnd = useCallback((event: SlideEvent) => {
        event.stopPropagation();
        if (!isDragging) {
            return;
        }
        
        const diff = handleLeft - handleRight;
        if (value === undefined && Math.abs(diff) < railWidthPx / 2) {
            setState((prevState) => ({
                ...prevState,
                isDragging: false,
            }));
            return;
        }

        const newValue = diff > 0;

        onChange(newValue);
        setState({
            ...getPositionFromValue(newValue, railWidthPx),
            isDragging: false,
            dragStartX: undefined,
        });
    }, [handleLeft, handleRight, isDragging, value, railWidthPx]);

    const onOnClick = useCallback(() => {
        if (disabled || isDragging || value === true) {
            return;
        }

        onChange(true);
    }, [disabled, isDragging, onChange, value]);

    const onOffClick = useCallback(() => {
        if (disabled || isDragging || value === false) {
            return;
        }

        onChange(false);
    }, [disabled, isDragging, onChange, value]);

    useEffect(() => {
        if (!isDragging) {
            return;
        }

        wnd.addEventListener("mousemove", onDragMove);
        wnd.addEventListener("touchmove", onDragMove, { passive: false });

        return () => {
            wnd.removeEventListener("mousemove", onDragMove);
            wnd.removeEventListener("touchmove", onDragMove);
        };
    }, [onDragMove, isDragging]);

    // Prevent scrolling during drag
    useEffect(() => {
        if (!isDragging) {
            return;
        }

        const preventScroll = (e: Event) => {
            e.preventDefault();
        };

        // Prevent all scroll-related events
        wnd.addEventListener("scroll", preventScroll, { passive: false });
        wnd.addEventListener("touchstart", preventScroll, { passive: false });
        wnd.addEventListener("wheel", preventScroll, { passive: false });
        document.body.style.overflow = "hidden";

        return () => {
            wnd.removeEventListener("scroll", preventScroll);
            wnd.removeEventListener("touchstart", preventScroll);
            wnd.removeEventListener("wheel", preventScroll);
            document.body.style.overflow = "";
        };
    }, [isDragging, wnd]);

    useEffect(() => {
        const updateRailWidth = () => {
            if (!rootEl.current) {
                return;
            }

            const railEl = rootEl.current.querySelector(".DecisionToggle-rail");
            if (!railEl) {
                return;
            }

            const railWidth = railEl.getBoundingClientRect().width;
            setRailWidthPx(railWidth);

            if (isDragging) {
                return;
            }

            // Update handle positions based on the new rail width
            setState((prevState) => ({
                ...prevState,
                ...getPositionFromValue(value, railWidth),
            }));
        };

        const intervalId = setInterval(updateRailWidth, 250);
        setTimeout(updateRailWidth, 1);

        return () => {
            clearInterval(intervalId);
        };
    }, [rootEl, value, isDragging]);

    useEffect(() => {
        if (!isDragging) {
            return;
        }

        wnd.addEventListener("mouseup", onDragEnd);
        wnd.addEventListener("touchend", onDragEnd, { passive: false });

        return () => {
            wnd.removeEventListener("mouseup", onDragEnd);
            wnd.removeEventListener("touchend", onDragEnd);
        };
    }, [isDragging, wnd, onDragEnd]);

    useEffect(() => {
        if (isDragging) {
            return;
        }

        setState({
            ...getPositionFromValue(value, railWidthPx),
            isDragging: false,
        });
    }, [value, isDragging, railWidthPx]);

    const isOn = handleRight === 0 && !isDragging;
    const isOff = handleLeft === 0 && !isDragging;

    const className = classNames("DecisionToggle", {
        "DecisionToggle-disabled": disabled,
        "DecisionToggle-dragging": isDragging,
        "DecisionToggle-on": isOn,
        "DecisionToggle-off": isOff,
    });

    const containerStyle = useMemo<CSSProperties | undefined>(() => fullWidth
        ? { width: "100%" }
        : undefined
    , [fullWidth]);

    return (
        <div className={className}
             ref={rootEl}
             style={containerStyle}>
            <div className="DecisionToggle-rail"
                 style={containerStyle}>
                <div className="DecisionToggle-handle"
                     onMouseDown={onDragStart}
                     onTouchStart={onDragStart}
                     style={{
                         left: handleLeft,
                         right: handleRight,
                     }} />
                <div className="DecisionToggle-icon handleIndicator">
                    <Visage2Icon color="dusk-grey"
                                 icon="menu-1"
                                 rotation={90}
                                 size="small"
                                 type="outline" />
                </div>
                <div className="DecisionToggle-button DecisionToggle-offButton"
                     onClick={clickable ? onOffClick : undefined}
                     role="button">
                    <Visage2Icon color={isOff ? "white" : "salmon"}
                                 icon="close-circle"
                                 size="medium"
                                 type="bold" />
                </div>
                <div className="DecisionToggle-button DecisionToggle-onButton"
                     onClick={clickable ? onOnClick : undefined}
                     role="button">
                    <Visage2Icon color={isOn ? "white" : "green"}
                                 icon="tick-circle"
                                 size="medium"
                                 type="bold" />
                </div>
            </div>
        </div>
    );
}

export default DecisionToggle;

